import { useEffect, useState } from 'react';
import facilityCard1 from './assets/news/news3.jpg';
import facilityCard2 from './assets/news/news5.jpg';
import facilityCard3 from './assets/news/news6.jpg';
import facilityCard4 from './assets/news/news8.jpg';

function FacilityDetailPage({ facilityId, onNavigate }) {
  const [cmsFacilitiesData, setCmsFacilitiesData] = useState({});

  useEffect(() => {
    let isMounted = true;
    const fetchCmsData = async () => {
      try {
        const apiBase = process.env.NODE_ENV === 'production'
          ? 'https://permataharapansecondary.sch.id/api'
          : (window.location.hostname === 'localhost' ? 'https://permataharapansecondary.sch.id/api' : '/api');
        const res = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=facilities&_t=${Date.now()}`);
        if (res.ok) {
          const json = await res.json();
          const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
          const found = items.find(s => s.section_type === 'facilities-overview-details' || s.section_type === 'facility-details');
          if (found && isMounted) {
            let content = found.content_published || found.content;
            if (typeof content === 'string') {
              try { content = JSON.parse(content); } catch (e) { content = {}; }
            }
            if (content && typeof content === 'object') {
              setCmsFacilitiesData(content);
            }
          }
        }
      } catch (err) {
        console.warn('[FacilityDetailPage] CMS fetch error:', err);
      }
    };
    fetchCmsData();
    return () => { isMounted = false; };
  }, []);

  const resolveImage = (img) => {
    if (!img) return null;
    if (typeof img !== 'string') return img;
    if (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('blob:') || img.startsWith('data:')) {
      return img;
    }
    const origin = process.env.NODE_ENV === 'production'
      ? 'https://permataharapansecondary.sch.id'
      : (typeof window !== 'undefined' && window.location.hostname === 'localhost' ? 'https://permataharapansecondary.sch.id' : '');
    return `${origin}${img.startsWith('/') ? '' : '/'}${img}`;
  };
  const [activeImageSlide, setActiveImageSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [facilityId]);

  const facilitiesData = {
    classrooms: {
      title: 'Classrooms',
      subtitle: 'Bright, Tech-Ready Learning Spaces',
      description: 'Our classrooms are designed to inspire curiosity and support collaborative learning with modern technology and flexible seating arrangements.',
      color: '#3b82f6',
      colorLight: '#dbeafe',
      icon: 'fa-chalkboard-user',
      images: [facilityCard1, facilityCard2, facilityCard3],
      features: [
        {
          icon: 'fa-laptop',
          title: 'Interactive Displays',
          detail: 'Smart boards and projectors for engaging multimedia lessons'
        },
        {
          icon: 'fa-chair',
          title: 'Flexible Seating',
          detail: 'Modular furniture that adapts to different learning activities'
        },
        {
          icon: 'fa-wifi',
          title: 'High-Speed Internet',
          detail: 'Reliable connectivity for research and online learning'
        },
        {
          icon: 'fa-lightbulb',
          title: 'Natural Lighting',
          detail: 'Large windows for natural light and student wellbeing'
        }
      ],
      highlights: [
        'Capacity: 25-30 students per classroom',
        'Air-conditioned and climate-controlled',
        'Accessible for students with special needs',
        'Equipped with first aid and safety equipment'
      ],
      content: 'Our classrooms represent a commitment to creating optimal learning environments. Each room is thoughtfully designed with student comfort and engagement in mind. Teachers have access to the latest educational technology, while students benefit from ergonomic furniture and abundant natural light. The flexible layout allows for various teaching methodologies, from traditional lectures to collaborative group work and project-based learning.'
    },
    labs: {
      title: 'Science & ICT Labs',
      subtitle: 'Hands-On Learning & Innovation',
      description: 'State-of-the-art laboratories where students conduct experiments, code, and explore scientific concepts through practical inquiry.',
      color: '#10b981',
      colorLight: '#d1fae5',
      icon: 'fa-flask',
      images: [facilityCard2, facilityCard3, facilityCard4],
      features: [
        {
          icon: 'fa-flask',
          title: 'Science Lab',
          detail: 'Equipped for chemistry, physics, and biology experiments'
        },
        {
          icon: 'fa-code',
          title: 'Coding Lab',
          detail: 'Computers and software for programming and digital literacy'
        },
        {
          icon: 'fa-robot',
          title: 'Robotics Station',
          detail: 'Equipment for robotics projects and STEM challenges'
        },
        {
          icon: 'fa-microscope',
          title: 'Research Tools',
          detail: 'Microscopes, sensors, and data collection equipment'
        }
      ],
      highlights: [
        'Safety protocols and protective equipment provided',
        'Lab technician on duty during all sessions',
        'Regular maintenance and equipment updates',
        'Capacity: 20-25 students per session'
      ],
      content: 'Our labs are designed to transform theoretical knowledge into practical understanding. Students engage in hands-on experiments that reinforce classroom learning and develop critical thinking skills. The science lab features modern equipment for chemistry, physics, and biology investigations. The ICT lab provides access to computers, coding platforms, and robotics kits, preparing students for careers in technology and innovation. All labs maintain strict safety standards and are supervised by trained technicians.'
    },
    library: {
      title: 'Library & Learning Commons',
      subtitle: 'Knowledge Hub & Quiet Study Space',
      description: 'A welcoming sanctuary for reading, research, and collaborative learning with curated collections and digital resources.',
      color: '#8b5cf6',
      colorLight: '#ede9fe',
      icon: 'fa-book',
      images: [facilityCard3, facilityCard1, facilityCard2],
      features: [
        {
          icon: 'fa-book',
          title: 'Extensive Collection',
          detail: '5,000+ books across all subjects and reading levels'
        },
        {
          icon: 'fa-computer',
          title: 'Digital Resources',
          detail: 'Online databases, e-books, and research platforms'
        },
        {
          icon: 'fa-users',
          title: 'Study Groups',
          detail: 'Collaborative spaces for group projects and discussions'
        },
        {
          icon: 'fa-headphones',
          title: 'Media Center',
          detail: 'Audio-visual resources and multimedia equipment'
        }
      ],
      highlights: [
        'Quiet study areas and collaborative zones',
        'Librarian support for research guidance',
        'Extended hours for student access',
        'Comfortable seating and reading nooks'
      ],
      content: 'The library serves as the intellectual heart of our school. Beyond traditional book collections, we offer digital resources, research databases, and multimedia materials. Students can access quiet study areas for focused work or collaborative spaces for group projects. Our librarian provides research guidance and helps students develop information literacy skills. The library is designed to foster a love of reading and support academic excellence across all disciplines.'
    },
    'sports-field': {
      title: 'Sports Field & Athletic Facilities',
      subtitle: 'Excellence in Physical Education',
      description: 'Multi-purpose courts and fields designed for athletics, team sports, and comprehensive physical education programs.',
      color: '#f59e0b',
      colorLight: '#fef3c7',
      icon: 'fa-futbol',
      images: [facilityCard4, facilityCard2, facilityCard1],
      features: [
        {
          icon: 'fa-futbol',
          title: 'Soccer Field',
          detail: 'Full-size field with professional-grade turf'
        },
        {
          icon: 'fa-basketball',
          title: 'Basketball Courts',
          detail: 'Indoor and outdoor courts with proper markings'
        },
        {
          icon: 'fa-dumbbell',
          title: 'Fitness Area',
          detail: 'Equipment for strength training and conditioning'
        },
        {
          icon: 'fa-person-running',
          title: 'Track & Field',
          detail: 'Running track and field event areas'
        }
      ],
      highlights: [
        'Professional coaching staff',
        'Safety equipment and first aid stations',
        'Lighting for evening activities',
        'Spectator seating for events'
      ],
      content: 'Our sports facilities support a comprehensive athletic program that promotes physical fitness, teamwork, and sportsmanship. The multi-purpose field accommodates soccer, field hockey, and other sports. Indoor courts provide year-round access to basketball and volleyball. Professional coaches guide students through training sessions and competitive events. All facilities meet international standards and are maintained to the highest safety specifications. We believe in developing well-rounded students who value health and active lifestyles.'
    },
    'art-music-rooms': {
      title: 'Art & Music Studios',
      subtitle: 'Creative Expression & Performance',
      description: 'Dedicated creative spaces where students explore visual arts, music, and performance with professional-grade equipment.',
      color: '#ec4899',
      colorLight: '#fce7f3',
      icon: 'fa-palette',
      images: [facilityCard1, facilityCard3, facilityCard4],
      features: [
        {
          icon: 'fa-palette',
          title: 'Art Studio',
          detail: 'Painting, drawing, sculpture, and mixed media spaces'
        },
        {
          icon: 'fa-music',
          title: 'Music Rooms',
          detail: 'Practice rooms and ensemble rehearsal spaces'
        },
        {
          icon: 'fa-microphone',
          title: 'Performance Stage',
          detail: 'Theater with sound system and lighting'
        },
        {
          icon: 'fa-camera',
          title: 'Media Lab',
          detail: 'Video production and photography equipment'
        }
      ],
      highlights: [
        'Professional-grade instruments and materials',
        'Experienced art and music instructors',
        'Regular performances and exhibitions',
        'Access to digital creative tools'
      ],
      content: 'Our art and music studios nurture creative talent and self-expression. The art studio provides space for painting, drawing, sculpture, and mixed media projects. Music rooms are equipped with pianos, guitars, drums, and other instruments for individual practice and ensemble rehearsals. The performance stage hosts student concerts, plays, and showcases throughout the year. We believe that arts education develops critical thinking, emotional intelligence, and cultural appreciation. Students have opportunities to exhibit their work and perform for the school community.'
    }
  };

  const baseFacility = facilitiesData[facilityId] || facilitiesData.classrooms;
  const dynamicFacility = cmsFacilitiesData[facilityId] || {};
  const facility = {
    ...baseFacility,
    ...dynamicFacility,
    images: (dynamicFacility.images && dynamicFacility.images.length > 0)
      ? dynamicFacility.images.map(resolveImage)
      : baseFacility.images,
    features: (dynamicFacility.features && dynamicFacility.features.length > 0)
      ? dynamicFacility.features
      : baseFacility.features,
    highlights: (dynamicFacility.highlights && dynamicFacility.highlights.length > 0)
      ? dynamicFacility.highlights
      : baseFacility.highlights
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveImageSlide((current) => (current + 1) % facility.images.length);
    }, 5000);

    return () => clearInterval(intervalId);
  }, [facility.images.length]);

  return (
    <section className="facility-detail-page">
      {/* Hero Section */}
      <section className="facility-detail-hero" style={{ backgroundColor: facility.color }}>
        <div className="facility-detail-hero-pattern" />
        <div className="facility-detail-hero-image">
          <div className="facility-detail-hero-media" aria-hidden="true">
            {facility.images.map((image, index) => (
              <div
                key={`${facilityId}-${index}`}
                className={`facility-detail-slide ${index === activeImageSlide ? 'active' : ''}`}
              >
                <img src={image} alt={facility.title} />
              </div>
            ))}
          </div>
          <div className="facility-detail-hero-overlay" aria-hidden="true" />
        </div>
        <div className="facility-detail-hero-content">
          <button
            className="facility-detail-back-btn"
            onClick={() => onNavigate && onNavigate('facilities')}
            aria-label="Back to facilities"
          >
            <i className="fas fa-arrow-left" /> Back
          </button>
          <div className="facility-detail-hero-icon">
            <i className={`fas ${facility.icon}`} />
          </div>
          <h1 className="facility-detail-hero-title">{facility.title}</h1>
          <p className="facility-detail-hero-subtitle">{facility.subtitle}</p>
        </div>
        <div className="facility-detail-hero-dots">
          {facility.images.map((_, index) => (
            <button
              key={`dot-${index}`}
              type="button"
              className={`facility-detail-dot ${index === activeImageSlide ? 'active' : ''}`}
              onClick={() => setActiveImageSlide(index)}
              aria-label={`Show slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Main Content */}
      <section className="facility-detail-main">
        {/* About Section */}
        <div className="facility-detail-section">
          <div className="facility-detail-section-header">
            <h2>About {facility.title}</h2>
            <div className="facility-detail-section-line" style={{ backgroundColor: facility.color }} />
          </div>
          <p className="facility-detail-description">{facility.description}</p>
          <div className="facility-detail-highlight-box" style={{ backgroundColor: facility.colorLight, borderLeftColor: facility.color }}>
            <i className={`fas ${facility.icon}`} style={{ color: facility.color }} />
            <p>{facility.content}</p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="facility-detail-section">
          <div className="facility-detail-section-header">
            <h2>Key Features</h2>
            <div className="facility-detail-section-line" style={{ backgroundColor: facility.color }} />
          </div>
          <div className="facility-features-grid">
            {facility.features.map((feature, index) => (
              <div key={`feature-${index}`} className="facility-feature-card" style={{ borderTopColor: facility.color }}>
                <div 
                  className="facility-feature-icon" 
                  style={{ 
                    '--feature-color': facility.color,
                    '--feature-color-dark': facility.color + 'cc'
                  }}
                >
                  <i className={`fas ${feature.icon}`} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.detail}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Highlights */}
        <div className="facility-detail-section">
          <div className="facility-detail-section-header">
            <h2>Highlights & Specifications</h2>
            <div className="facility-detail-section-line" style={{ backgroundColor: facility.color }} />
          </div>
          <div className="facility-highlights-grid">
            {facility.highlights.map((highlight, index) => (
              <div key={`highlight-${index}`} className="facility-highlight-card" style={{ backgroundColor: facility.colorLight, borderLeftColor: facility.color }}>
                <div className="facility-highlight-icon" style={{ color: facility.color }}>
                  <i className="fas fa-check-circle" />
                </div>
                <p>{highlight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="facility-detail-cta" 
        style={{ 
          '--cta-color': facility.color,
          '--cta-color-dark': facility.color + 'cc'
        }}
      >
        <div className="facility-detail-cta-content">
          <h2>Ready to Experience {facility.title}?</h2>
          <p>Schedule a campus tour or learn more about our programs.</p>
          <button className="facility-detail-cta-btn" onClick={() => onNavigate && onNavigate('contact')}>
            Schedule a Tour <i className="fas fa-arrow-right" />
          </button>
        </div>
      </section>
    </section>
  );
}

export default FacilityDetailPage;
